import React, { useContext } from 'react';
import classNames from 'classnames';
import { Button, Modal } from 'react-bootstrap';
import PropTypes from 'prop-types';
import ModalContext from './ModalContext';
import '../../styles/modalwrapper.scss';

const ModalWrapper = ({
  children,
  close,
  closeButton = true,
  confirm = false,
  confirmLabels = ['Ok', 'Cancel'],
  defaultLabel = 'Close',
  dialogClass,
  footer,
  headerClass = 'bg-default',
  headerLink = '',
  hideFooter = false,
  hidePrint = false,
  itemName,
  naked = false,
  ok,
  render,
  show,
  size = 'lg',
  staticBackdrop = false,
  style = 'default',
  title,
  topIsClosing = false,
  topModal = true,
  view = '',
}) => {
  const { print } = useContext(ModalContext);

  const renderHeader = () => {
    const className = headerClass ? `modal-header ${headerClass}` : 'modal-header';

    return (
      <Modal.Header closeButton={closeButton} bsPrefix={className}>
        <Modal.Title>{title}</Modal.Title>
        <div className="modal-toolbar">
          {!hidePrint && render !== 'table' && (
            <div className="print" onClick={print}>
              <i className="zmdi zmdi-print" />
            </div>
          )}
          {headerLink && (
            <div className="print">
              <a href={headerLink} rel="noopener noreferrer" target="_blank" title="Open In New Window">
                <i className="zmdi zmdi-open-in-new" />
              </a>
            </div>
          )}
        </div>
      </Modal.Header>
    );
  };

  const renderFooter = () => {
    if (hideFooter) {
      return null;
    }

    if (footer) {
      return <Modal.Footer>{footer}</Modal.Footer>;
    }

    return (
      <Modal.Footer>
        {confirm ? (
          <div>
            <Button onClick={close} variant="secondary">
              {confirmLabels[1]}
            </Button>
            <Button onClick={ok} variant="primary">
              {confirmLabels[0]}
            </Button>
          </div>
        ) : (
          <Button onClick={close} variant="secondary">
            {defaultLabel}
          </Button>
        )}
      </Modal.Footer>
    );
  };

  const pageClass = view === 'listEditItem' ? `page-${itemName}` : '';
  const className = classNames('modal-wrapper', 'animatedFast', pageClass, {
    [view]: true,
    [render]: render !== 'alert', // alert clashes with bootstrap
    [style]: true,
    top: topModal || topIsClosing,
    under: !topModal,
    fadeInUp: show,
    naked,
  });

  const backdropValue = staticBackdrop ? 'static' : true;

  // topIsClosing is that any underlying backdrop animations don't just flash back into place while a modal is fading (exiting) out over it
  const backdropClassName = classNames({
    under: !topModal,
    in: topIsClosing,
    top: topModal,
  });

  return (
    <Modal
      aria-labelledby={`contained-modal-title-${size}`}
      backdrop={backdropValue}
      backdropClassName={backdropClassName}
      size={size}
      className={className}
      dialogClassName={dialogClass}
      onHide={close}
      show={show}
      // centered TODO
    >
      {renderHeader()}
      <Modal.Body>{children}</Modal.Body>
      {renderFooter()}
    </Modal>
  );
};

ModalWrapper.propTypes = {
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  close: PropTypes.func,
  closeButton: PropTypes.bool,
  confirm: PropTypes.bool,
  confirmLabels: PropTypes.array,
  defaultLabel: PropTypes.string,
  dialogClass: PropTypes.string,
  footer: PropTypes.element,
  headerClass: PropTypes.string,
  headerLink: PropTypes.string,
  hideFooter: PropTypes.bool,
  hidePrint: PropTypes.bool,
  itemName: PropTypes.string,
  naked: PropTypes.bool,
  ok: PropTypes.func,
  render: PropTypes.string,
  show: PropTypes.bool,
  size: PropTypes.string,
  staticBackdrop: PropTypes.bool,
  style: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  topIsClosing: PropTypes.bool,
  topModal: PropTypes.bool,
  view: PropTypes.string,
};

export default ModalWrapper;
